import type React from 'react';
import { useState, useEffect, useRef, type ReactNode } from 'react';
import { ModalContext } from './ModalContext';

// Simple Backdrop component with animation
const Backdrop = ({ children, onClose, isClosing }: { children: ReactNode; onClose: () => void; isClosing: boolean }) => {
  const [isActive, setIsActive] = useState(false);
  
  useEffect(() => {
    // Trigger animation after component mounts
    const timer = setTimeout(() => setIsActive(true), 10);
    return () => clearTimeout(timer);
  }, []);
  
  // Determine the active state based on both the internal state and the closing prop
  const activeState = isActive && !isClosing;

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
    <div 
      className={`fixed inset-0 z-50 transition-opacity duration-300 ${
        activeState ? 'opacity-100' : 'opacity-0'
      }`} 
      onClick={onClose}
    >
      {/* Content container with animation */}
      <div 
        className="relative z-10 h-full w-full flex items-center justify-center"
      >
        {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
        <div 
          onClick={(e) => e.stopPropagation()}
          className={`transform transition-all duration-300 pointer-events-auto ${
            activeState ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
          }`}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export const ModalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [content, setContent] = useState<ReactNode | null>(null);
  const [onDismissCallback, setOnDismissCallback] = useState<(() => void) | undefined>();
  const [isClosing, setIsClosing] = useState(false);
  
  // Always render the backdrop, but control its visibility with opacity
  const [backdropVisible, setBackdropVisible] = useState(false);

  // Queue for handling sequential modals
  const modalQueue = useRef<Array<{ content: ReactNode; onDismiss?: () => void }>>([]);
  
  // Flag to track if we're currently processing the queue
  const isProcessingQueue = useRef(false);

  // Add effect to disable scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      // Save current scroll position
      const scrollY = window.scrollY;
      
      // Apply fixed positioning with current scroll offset
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${scrollY}px`;
      
      return () => {
        // Restore positioning
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.top = '';
        
        // Restore scroll position
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  const closeModal = () => {
    // Immediately set isClosing to trigger the visual transition
    setIsClosing(true);
    
    // Immediately remove blur and darkening on close
    const backdropElement = document.querySelector('.modal-backdrop');
    if (backdropElement) {
      backdropElement.classList.remove('backdrop-blur-sm');
      backdropElement.classList.remove('bg-black/40');
      backdropElement.classList.add('bg-black/0');
    }
    
    // Run callback immediately so any logic can execute right away
    if (onDismissCallback) {
      onDismissCallback();
      setOnDismissCallback(undefined);
    }
    
    // Only delay the actual DOM removal
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
      
      // After another small delay, hide the backdrop completely
      setTimeout(() => {
        setBackdropVisible(false);
        
        // Check if there are pending modals in the queue
        processNextInQueue();
      }, 50); // Shorter delay for complete removal
    }, 300); // Match this with the duration in your transition classes
  };

  // New function to close modal without animation
  const closeModalWithoutAnimation = () => {
    // Run callback immediately
    if (onDismissCallback) {
      onDismissCallback();
      setOnDismissCallback(undefined);
    }
    
    // Immediately close the modal without waiting for animations
    setIsOpen(false);
    setIsClosing(false);
    setBackdropVisible(false);
    
    // Process the next modal in the queue if any
    processNextInQueue();
  };

  // Process the next modal in queue if any
  const processNextInQueue = () => {
    if (modalQueue.current.length > 0 && !isProcessingQueue.current) {
      isProcessingQueue.current = true;
      const nextModal = modalQueue.current.shift();
      
      if (nextModal) {
        // Actually open the modal
        handleOpenModal(nextModal.content, nextModal.onDismiss);
      }
    } else {
      isProcessingQueue.current = false;
    }
  };

  // Internal function to actually open a modal
  const handleOpenModal = (modalContent: ReactNode, onDismiss?: () => void) => {
    // First make backdrop visible but transparent
    setBackdropVisible(true);
    
    // Then in the next tick, set everything else to trigger animations
    setTimeout(() => {
      setContent(modalContent);
      setOnDismissCallback(() => onDismiss);
      setIsClosing(false);
      setIsOpen(true);
      
      // Reset processing flag after modal is opened
      isProcessingQueue.current = false;
    }, 0);
  };

  // Public method for opening a modal - this handles queue management
  const openModal = (modalContent: ReactNode, onDismiss?: () => void) => {
    if (isOpen) {
      // If a modal is already open, add the new one to the queue
      modalQueue.current.push({ content: modalContent, onDismiss });
      
      // Close the current modal, which will trigger processing the queue when done
      closeModal();
    } else {
      // If no modal is open, open it directly
      handleOpenModal(modalContent, onDismiss);
    }
  };

  return (
    <ModalContext.Provider value={{ openModal, closeModal, closeModalWithoutAnimation, isModalOpen: isOpen }}>
      {/* Wrap children in a div that disables all interactions when modal is open */}
      <div className={isOpen ? 'pointer-events-none touch-none' : ''}>
        {children}
      </div>
      
      {/* Always render backdrop but control with opacity */}
      {backdropVisible && (
        <div className={`fixed inset-0 z-50 modal-backdrop pointer-events-auto ${
          isOpen && !isClosing ? 'backdrop-blur-sm bg-black/40' : 'bg-black/0'
        } transition-colors duration-150`} />
      )}
      
      {isOpen && (
        <Backdrop onClose={closeModal} isClosing={isClosing}>
          {content}
        </Backdrop>
      )}
    </ModalContext.Provider>
  );
};